import { DailyFieldReport, DfrPayload } from '../api/dfrService';
import { Task, TaskDefect } from '../api/tasksService';
import { DefectBoardRow, DefectsBoard, DefectSeverityFilter, DefectStatusFilter } from '../api/defectService';
import { Timesheet, TimesheetPayload, Worker } from '../api/timesheetService';
import { AttendancePayload, AttendanceRecord } from '../api/attendanceService';
import { TeamListItem } from '../api/teamService';
import { QcItemResult, QcItemResultValue, QcOverallResult, QcRun } from '../api/qcChecklistService';
import { fieldDb, FieldCacheRecord, FieldEntityType, FieldOutboxAction } from './fieldDb';

const nowIso = () => new Date().toISOString();
const uuid = () => crypto.randomUUID();
const localId = (clientUuid: string) => `local:${clientUuid}`;

const BACKEND_LIFECYCLE_TO_FRONTEND: Record<string, string> = {
  draft: 'DRAFT',
  scheduled: 'SCHEDULED',
  waiting: 'WAITING',
  blocked: 'BLOCKED',
  ready: 'READY',
  in_progress: 'IN_PROGRESS',
  paused: 'PAUSED',
  inspection_pending: 'INSPECTION_PENDING',
  inspection_failed: 'INSPECTION_FAILED',
  inspection_passed: 'INSPECTION_PASSED',
  cancelled: 'CANCELLED',
};

const PRIORITY_TO_FRONTEND: Record<string, Task['priority']> = {
  low: 'LOW',
  normal: 'MEDIUM',
  medium: 'MEDIUM',
  high: 'HIGH',
  critical: 'URGENT',
  urgent: 'URGENT',
};

export async function cachedRecords<T>(entityType: FieldEntityType, projectId?: string | null): Promise<T[]> {
  const rows = await fieldDb.fieldCache.where('entityType').equals(entityType).toArray();
  return rows
    .filter((row) => !projectId || row.projectId === projectId)
    .map((row) => row.payload as T);
}

export async function cachedTeams(): Promise<TeamListItem[]> {
  return cachedRecords<TeamListItem>('team');
}

export async function cachedWorkers(): Promise<Worker[]> {
  return cachedRecords<Worker>('worker');
}

export async function cachedTasks(projectId?: string | null): Promise<Task[]> {
  const rows = await cachedRecords<Record<string, any>>('task', projectId);
  return rows.map(toFrontendTask);
}

export async function cachedTaskById(id: string): Promise<Task | null> {
  const row = await fieldDb.fieldCache.get(`task:${id}`);
  return row ? toFrontendTask(row.payload as Record<string, any>) : null;
}

export async function cachedQcRunById(id: string): Promise<QcRun | null> {
  const row = await fieldDb.fieldCache.get(`qc_run:${id}`);
  return row ? row.payload as QcRun : null;
}

export async function cachedDefectsBoard(
  projectId: string,
  filters: {
    status?: DefectStatusFilter;
    severity?: DefectSeverityFilter;
    activeOnly?: boolean;
  } = {}
): Promise<DefectsBoard> {
  const [defectRecords, taskRows] = await Promise.all([
    cachedRecords<Record<string, any>>('defect'),
    cachedTasks(projectId),
  ]);
  const tasksById = new Map(taskRows.map((task) => [task.id, task]));
  const rows = defectRecords
    .filter((record) => {
      const taskId = record.originalTaskId ?? record.original_task_id;
      const task = taskId ? tasksById.get(taskId) : null;
      return task?.projectId === projectId;
    })
    .map((record) => toDefectBoardRow(record, tasksById))
    .filter((row) => filters.status && filters.status !== 'all' ? row.status === filters.status : true)
    .filter((row) => filters.severity && filters.severity !== 'all' ? row.severity === filters.severity : true)
    .filter((row) => filters.activeOnly ? !['closed_passed', 'closed_disputed', 'void'].includes(row.status) : true);

  return {
    defects: rows,
    totals: {
      count: rows.length,
      open: rows.filter((row) => row.status === 'open').length,
      inProgress: rows.filter((row) => ['assigned', 'fixing'].includes(row.status)).length,
      awaitingRecheck: rows.filter((row) => row.status === 'recheck_pending').length,
      closedPassed: rows.filter((row) => row.status === 'closed_passed').length,
      closedDisputed: rows.filter((row) => row.status === 'closed_disputed').length,
      voided: rows.filter((row) => row.status === 'void').length,
      bySeverity: {
        low: rows.filter((row) => row.severity === 'low').length,
        medium: rows.filter((row) => row.severity === 'medium').length,
        high: rows.filter((row) => row.severity === 'high').length,
        critical: rows.filter((row) => row.severity === 'critical').length,
      },
    },
  };
}

export function mergeById<T extends { id: string }>(serverRows: T[], cachedRows: T[]): T[] {
  const rows = new Map<string, T>();
  serverRows.forEach((row) => rows.set(row.id, row));
  cachedRows.forEach((row) => rows.set(row.id, row));
  return Array.from(rows.values());
}

export function outboxEntityIdFor(actionType: string, entityId: string): string | null {
  return actionType === 'create' ? null : entityId;
}

export function qcOverallResultFor(results: Array<Pick<QcItemResult, 'result'>>): QcOverallResult {
  return results.some((result) => result.result === 'fail') ? 'fail' : 'pass';
}

export async function enqueueOfflineDfr(payload: DfrPayload, submitAfter: boolean): Promise<DailyFieldReport> {
  const clientUuid = payload.clientUuid ?? uuid();
  const createdAt = nowIso();
  const entries = (payload.entries ?? [])
    .filter((entry) => entry.description.trim())
    .map((entry) => {
      const entryClientUuid = entry.clientUuid ?? uuid();
      return {
        id: localId(entryClientUuid),
        dfrId: localId(clientUuid),
        taskId: entry.taskId || null,
        entryType: entry.entryType,
        description: entry.description.trim(),
        documentId: entry.documentId || null,
        fieldEvidenceId: entry.fieldEvidenceId || null,
        clientUuid: entryClientUuid,
        createdAt,
      };
    });
  const report: DailyFieldReport = {
    id: localId(clientUuid),
    projectId: payload.projectId,
    reportDate: payload.reportDate,
    authorUserId: null,
    weather: payload.weather ?? null,
    temperatureC: payload.temperatureC ?? null,
    manpowerCount: Number(payload.manpowerCount ?? 0),
    status: submitAfter ? 'submitted' : 'draft',
    workSummary: payload.workSummary ?? null,
    delaysConstraints: payload.delaysConstraints ?? null,
    submittedAt: submitAfter ? createdAt : null,
    entries,
    clientUuid,
    createdAt,
    updatedAt: createdAt,
  } as DailyFieldReport;

  await putCachedRecord('daily_field_report', report.id, payload.projectId, report);
  await fieldDb.fieldOutbox.put({
    clientUuid,
    entityType: 'daily_field_report',
    entityId: outboxEntityIdFor('create', report.id),
    actionType: 'create',
    projectId: payload.projectId,
    payload: {
      ...payload,
      clientUuid,
      status: report.status,
      submittedAt: report.submittedAt,
      entries: entries.map((entry) => ({
        taskId: entry.taskId,
        entryType: entry.entryType,
        description: entry.description,
        documentId: entry.documentId,
        fieldEvidenceId: entry.fieldEvidenceId,
        clientUuid: entry.clientUuid,
      })),
    },
    status: 'pending',
    attemptCount: 0,
    createdAt,
    updatedAt: createdAt,
  });

  return report;
}

export async function enqueueOfflineTimesheet(payload: TimesheetPayload, submitAfter: boolean): Promise<Timesheet> {
  const clientUuid = payload.clientUuid ?? uuid();
  const createdAt = nowIso();
  const entries = (payload.entries ?? [])
    .filter((entry) => entry.workerId && Number(entry.hours) >= 0)
    .map((entry) => {
      const entryClientUuid = entry.clientUuid ?? uuid();
      return {
        id: localId(entryClientUuid),
        timesheetId: localId(clientUuid),
        workerId: entry.workerId,
        taskId: entry.taskId || null,
        hours: Number(entry.hours),
        hourType: entry.hourType ?? 'regular',
        notes: entry.notes || null,
        clientUuid: entryClientUuid,
        createdAt,
      };
    });
  const timesheet: Timesheet = {
    id: localId(clientUuid),
    projectId: payload.projectId,
    workDate: payload.workDate,
    teamId: payload.teamId,
    submittedByUserId: null,
    status: submitAfter ? 'submitted' : 'draft',
    submittedAt: submitAfter ? createdAt : null,
    approvedByUserId: null,
    approvedAt: null,
    entries,
    clientUuid,
    createdAt,
    updatedAt: createdAt,
  } as Timesheet;

  await putCachedRecord('timesheet', timesheet.id, payload.projectId, timesheet);
  await fieldDb.fieldOutbox.put({
    clientUuid,
    entityType: 'timesheet',
    entityId: outboxEntityIdFor('create', timesheet.id),
    actionType: 'create',
    projectId: payload.projectId,
    payload: {
      ...payload,
      clientUuid,
      status: timesheet.status,
      submittedAt: timesheet.submittedAt,
      entries: entries.map((entry) => ({
        workerId: entry.workerId,
        taskId: entry.taskId,
        hours: entry.hours,
        hourType: entry.hourType,
        notes: entry.notes,
        clientUuid: entry.clientUuid,
      })),
    },
    status: 'pending',
    attemptCount: 0,
    createdAt,
    updatedAt: createdAt,
  });

  return timesheet;
}

export async function enqueueOfflineAttendance(payload: AttendancePayload, worker?: Worker | null): Promise<AttendanceRecord> {
  const clientUuid = payload.clientUuid ?? uuid();
  const timestamp = payload.scanOutAt ?? payload.scanInAt ?? nowIso();
  const existingOutbox = await findPendingOutboxAction<AttendancePayload>(
    'attendance_record',
    (action) =>
      action.payload.workerId === payload.workerId &&
      action.payload.projectId === payload.projectId &&
      action.payload.attendanceDate === payload.attendanceDate
  );
  const existingRecord = await fieldDb.fieldCache
    .where('entityType')
    .equals('attendance_record')
    .toArray()
    .then((records) => records.find((record) => {
      const row = record.payload as AttendanceRecord;
      return row.workerId === payload.workerId &&
        row.projectId === payload.projectId &&
        row.attendanceDate === payload.attendanceDate;
    }));
  const base = existingRecord?.payload as AttendanceRecord | undefined;
  const record: AttendanceRecord = {
    id: base?.id ?? localId(clientUuid),
    workerId: payload.workerId,
    projectId: payload.projectId,
    attendanceDate: payload.attendanceDate,
    scanInAt: base?.scanInAt ?? payload.scanInAt ?? timestamp,
    scanOutAt: payload.scanOutAt ?? base?.scanOutAt ?? null,
    method: payload.method ?? base?.method ?? 'badge',
    clientUuid: base?.clientUuid ?? clientUuid,
    notes: payload.notes ?? base?.notes ?? null,
    worker: worker ? {
      id: worker.id,
      name: worker.name,
      badgeCode: worker.badgeCode,
      teamId: worker.teamId,
      trade: worker.trade,
    } : base?.worker,
    createdAt: base?.createdAt ?? timestamp,
    updatedAt: timestamp,
  };

  await putCachedRecord('attendance_record', record.id, payload.projectId, record);
  await upsertOutboxAction(existingOutbox, {
    clientUuid: existingOutbox?.clientUuid ?? clientUuid,
    entityType: 'attendance_record',
    entityId: outboxEntityIdFor('create', record.id),
    actionType: 'create',
    projectId: payload.projectId,
    payload: {
      ...payload,
      clientUuid: existingOutbox?.clientUuid ?? clientUuid,
      scanInAt: record.scanInAt,
      scanOutAt: record.scanOutAt,
    },
    status: 'pending',
    attemptCount: existingOutbox?.attemptCount ?? 0,
    createdAt: existingOutbox?.createdAt ?? timestamp,
    updatedAt: timestamp,
  });

  return record;
}

export async function enqueueOfflineTaskPatch(
  task: Task,
  syncPayload: Record<string, unknown>,
  cachePatch: Partial<Task>
): Promise<Task> {
  const clientUuid = uuid();
  const updatedAt = nowIso();
  const updated = {
    ...task,
    ...cachePatch,
    updatedAt,
  };

  await putCachedRecord('task', task.id, task.projectId, updated);
  await fieldDb.fieldOutbox.put({
    clientUuid,
    entityType: 'task',
    entityId: task.id,
    actionType: 'update',
    projectId: task.projectId,
    payload: { id: task.id, ...syncPayload },
    baseVersionAt: task.updatedAt,
    status: 'pending',
    attemptCount: 0,
    createdAt: updatedAt,
    updatedAt,
  });

  return updated;
}

export async function enqueueOfflineFieldEvidence(
  task: Task,
  payload: {
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    dataUrl: string;
    notes?: string | null;
  }
): Promise<void> {
  const clientUuid = uuid();
  const createdAt = nowIso();
  const evidenceId = localId(clientUuid);
  const evidence = {
    id: evidenceId,
    taskId: task.id,
    wbsId: (task as any).wbsId ?? (task as any).wbs_id ?? null,
    costCodeId: (task as any).costCodeId ?? (task as any).cost_code_id ?? null,
    evidenceType: 'photo',
    captureMethod: 'worker_mobile',
    capturedAt: createdAt,
    notes: payload.notes ?? 'Field Mode task photo evidence',
    metadata: {
      fileName: payload.fileName,
      mimeType: payload.mimeType,
      sizeBytes: payload.sizeBytes,
      dataUrl: payload.dataUrl,
      source: 'field_mode_task_photo',
    },
    verificationStatus: 'submitted',
    clientUuid,
    createdAt,
    updatedAt: createdAt,
  };

  await putCachedRecord('field_evidence', evidenceId, task.projectId, evidence);
  await fieldDb.fieldOutbox.put({
    clientUuid,
    entityType: 'field_evidence',
    entityId: evidenceId,
    actionType: 'create',
    projectId: task.projectId,
    payload: evidence,
    status: 'pending',
    attemptCount: 0,
    createdAt,
    updatedAt: createdAt,
  });
}

export async function enqueueOfflineDefect(
  task: Task,
  payload: { title: string; description?: string; severity?: 'low' | 'medium' | 'high' | 'critical' }
): Promise<{ task: Task; defect: TaskDefect }> {
  const clientUuid = uuid();
  const createdAt = nowIso();
  const defect: TaskDefect = {
    id: localId(clientUuid),
    title: payload.title,
    description: payload.description ?? null,
    severity: payload.severity ?? 'medium',
    status: 'open',
    createdAt,
    updatedAt: createdAt,
  };
  const defectRecord = {
    ...payload,
    id: defect.id,
    originalTaskId: task.id,
    status: 'open',
    severity: defect.severity,
    raisedById: null,
    createdAt,
    updatedAt: createdAt,
    clientUuid,
  };
  const updatedTask = {
    ...task,
    defects: [...(task.defects ?? []), defect],
    updatedAt: createdAt,
  };

  await putCachedRecord('defect', defect.id, task.projectId, defectRecord);
  await putCachedRecord('task', task.id, task.projectId, updatedTask);
  await fieldDb.fieldOutbox.put({
    clientUuid,
    entityType: 'defect',
    entityId: outboxEntityIdFor('create', defect.id),
    actionType: 'create',
    projectId: task.projectId,
    payload: {
      ...payload,
      originalTaskId: task.id,
      status: 'open',
      severity: defect.severity,
      clientUuid,
    },
    status: 'pending',
    attemptCount: 0,
    createdAt,
    updatedAt: createdAt,
  });

  return { task: updatedTask, defect };
}

export async function cacheQcRun(run: QcRun): Promise<void> {
  await putCachedRecord('qc_run', run.id, run.projectId, run);
}

export async function enqueueOfflineQcItemResult(
  run: QcRun,
  itemIndex: number,
  patch: {
    result?: QcItemResultValue;
    notes?: string | null;
    evidenceDocumentId?: string | null;
    evidenceFieldId?: string | null;
  }
): Promise<QcRun> {
  const existingOutbox = await findPendingOutboxAction<Record<string, any>>(
    'qc_checklist_item_result',
    (action) => action.payload.runId === run.id && Number(action.payload.itemIndex) === itemIndex
  );
  const clientUuid = existingOutbox?.clientUuid ?? uuid();
  const updatedAt = nowIso();
  const current = (run.itemResults ?? []).find((result) => result.itemIndex === itemIndex);
  const existingPayload = existingOutbox?.payload ?? {};
  const itemResult: QcItemResult = {
    id: current?.id ?? localId(clientUuid),
    runId: run.id,
    itemIndex,
    result: patch.result ?? existingPayload.result ?? current?.result ?? 'pass',
    notes: patch.notes ?? existingPayload.notes ?? current?.notes ?? null,
    evidenceDocumentId: patch.evidenceDocumentId ?? existingPayload.evidenceDocumentId ?? current?.evidenceDocumentId ?? null,
    evidenceFieldId: patch.evidenceFieldId ?? existingPayload.evidenceFieldId ?? current?.evidenceFieldId ?? null,
    recordedAt: updatedAt,
  };
  const itemResults = [
    ...(run.itemResults ?? []).filter((result) => result.itemIndex !== itemIndex),
    itemResult,
  ].sort((a, b) => a.itemIndex - b.itemIndex);
  const updatedRun = { ...run, itemResults, updatedAt } as QcRun;
  const payload = {
    runId: run.id,
    itemIndex,
    result: itemResult.result,
    notes: itemResult.notes,
    evidenceDocumentId: itemResult.evidenceDocumentId,
    evidenceFieldId: itemResult.evidenceFieldId,
    recordedAt: itemResult.recordedAt,
    clientUuid,
  };

  await putCachedRecord('qc_run', run.id, run.projectId, updatedRun);
  await upsertOutboxAction(existingOutbox, {
    clientUuid,
    entityType: 'qc_checklist_item_result',
    entityId: outboxEntityIdFor('create', itemResult.id),
    actionType: 'create',
    projectId: run.projectId,
    payload,
    status: 'pending',
    attemptCount: existingOutbox?.attemptCount ?? 0,
    createdAt: existingOutbox?.createdAt ?? updatedAt,
    updatedAt,
  });

  return updatedRun;
}

export async function enqueueOfflineQcRunPatch(
  run: QcRun,
  syncPayload: Record<string, unknown>,
  cachePatch: Partial<QcRun>
): Promise<QcRun> {
  const existingOutbox = await findPendingOutboxAction<Record<string, any>>(
    'qc_run',
    (action) => action.entityId === run.id
  );
  const clientUuid = existingOutbox?.clientUuid ?? uuid();
  const updatedAt = nowIso();
  const payload = {
    ...(existingOutbox?.payload ?? {}),
    id: run.id,
    ...syncPayload,
  };
  const updatedRun = {
    ...run,
    ...cachePatch,
    updatedAt,
  } as QcRun;

  await putCachedRecord('qc_run', run.id, run.projectId, updatedRun);
  await upsertOutboxAction(existingOutbox, {
    clientUuid,
    entityType: 'qc_run',
    entityId: run.id,
    actionType: 'update',
    projectId: run.projectId,
    payload,
    baseVersionAt: existingOutbox?.baseVersionAt ?? ((run as any).updatedAt ?? run.completedAt ?? run.startedAt ?? updatedAt),
    status: 'pending',
    attemptCount: existingOutbox?.attemptCount ?? 0,
    createdAt: existingOutbox?.createdAt ?? updatedAt,
    updatedAt,
  });

  return updatedRun;
}

export async function putCachedRecord<T>(
  entityType: FieldEntityType,
  entityId: string,
  projectId: string | null | undefined,
  payload: T
) {
  const cachedAt = nowIso();
  const record: FieldCacheRecord<T> = {
    key: `${entityType}:${entityId}`,
    entityType,
    entityId,
    projectId,
    payload,
    cachedAt,
    serverUpdatedAt: null,
  };
  await fieldDb.fieldCache.put(record);
}

async function findPendingOutboxAction<T>(
  entityType: FieldEntityType,
  predicate: (action: FieldOutboxAction<T>) => boolean
): Promise<FieldOutboxAction<T> | null> {
  const actions = await fieldDb.fieldOutbox.where('entityType').equals(entityType).toArray() as FieldOutboxAction<T>[];
  return actions.find((action) => ['pending', 'failed'].includes(action.status) && predicate(action)) ?? null;
}

async function upsertOutboxAction<T>(
  existing: FieldOutboxAction<T> | null | undefined,
  action: FieldOutboxAction<T>
): Promise<void> {
  if (existing?.id) {
    await fieldDb.fieldOutbox.update(existing.id, {
      ...action,
      id: existing.id,
      errorMessage: null,
      syncedAt: null,
    });
    return;
  }

  await fieldDb.fieldOutbox.put(action);
}

function toFrontendTask(task: Record<string, any>): Task {
  const status = normalizeStatus(task.lifecycleStatus ?? task.lifecycle_status ?? task.status);
  return {
    id: String(task.id),
    title: task.title || task.name || 'Untitled task',
    taskCode: task.taskCode ?? task.task_code,
    taskType: task.taskType ?? task.task_type,
    description: task.description ?? null,
    status,
    lifecycleStatus: status,
    paymentStatus: task.paymentStatus ?? task.payment_status,
    priority: normalizePriority(task.priority),
    startDate: task.startDate ?? task.plannedStartDate ?? task.planned_start_date ?? null,
    dueDate: task.dueDate ?? task.plannedEndDate ?? task.planned_end_date ?? null,
    completionDate: task.completionDate ?? task.actualEndDate ?? task.actual_end_date ?? null,
    percentComplete: task.percentComplete ?? task.completionPercentage ?? task.completion_percentage ?? 0,
    estimatedHours: task.estimatedHours ?? task.estimatedDurationHours ?? task.estimated_duration_hours ?? null,
    actualHours: task.actualHours ?? task.actualDurationHours ?? task.actual_duration_hours ?? null,
    materialsReady: task.materialsReady ?? task.materials_ready,
    storageLocationId: task.storageLocationId ?? task.storage_location_id ?? null,
    blockedReason: task.blockedReason ?? task.blocked_reason ?? null,
    assignedToId: task.assignedToId ?? task.assigned_to_id ?? null,
    assignedTo: task.assignedTo ?? task.assigned_to,
    teamId: task.teamId ?? task.team_id ?? null,
    team: task.team,
    teamAssignments: task.teamAssignments ?? task.team_assignments ?? [],
    projectId: task.projectId ?? task.project_id ?? null,
    project: task.project,
    phaseId: task.phaseId ?? task.phase_id ?? null,
    phase: task.phase,
    blockId: task.blockId ?? task.block_id ?? null,
    buildingId: task.buildingId ?? task.building_id ?? null,
    unitId: task.unitId ?? task.unit_id ?? null,
    workLocation: task.workLocation ?? task.work_location ?? null,
    workLocationId: task.workLocationId ?? task.work_location_id ?? null,
    workLocationRef: task.workLocationRef ?? task.work_location_ref ?? null,
    specifications: Array.isArray(task.specifications) ? task.specifications : [],
    conditions: Array.isArray(task.conditions) ? task.conditions : [],
    component: task.component ?? null,
    resourcesRequired: task.resourcesRequired ?? task.resources_required ?? [],
    defects: task.defects ?? [],
    history: task.history ?? [],
    notes: task.notes ?? null,
    createdAt: task.createdAt ?? task.created_at ?? nowIso(),
    updatedAt: task.updatedAt ?? task.updated_at ?? nowIso(),
  };
}

function toDefectBoardRow(defect: Record<string, any>, tasksById: Map<string, Task>): DefectBoardRow {
  const taskId = defect.originalTaskId ?? defect.original_task_id;
  const task = taskId ? tasksById.get(taskId) : null;
  const createdAt = defect.createdAt ?? defect.created_at ?? nowIso();
  return {
    id: String(defect.id),
    title: defect.title ?? 'Untitled defect',
    description: defect.description ?? null,
    status: defect.status ?? 'open',
    severity: defect.severity ?? 'medium',
    defectType: defect.defectType ?? defect.defect_type ?? null,
    createdAt,
    closedAt: defect.closedAt ?? defect.closed_at ?? null,
    ageDays: ageDays(createdAt),
    resolutionNotes: defect.resolutionNotes ?? defect.resolution_notes ?? null,
    originalTaskId: taskId,
    originalTaskCode: task?.taskCode ?? taskId,
    originalTaskName: task?.title ?? 'Cached task',
    reworkCount: defect.reworkCount ?? defect.rework_count ?? 0,
    remedialTaskId: defect.remedialTaskId ?? defect.remedial_task_id ?? null,
    remedialTaskCode: defect.remedialTaskCode ?? defect.remedial_task_code ?? null,
    remedialTaskStatus: defect.remedialTaskStatus ?? defect.remedial_task_status ?? null,
    unitId: defect.unitId ?? defect.unit_id ?? task?.unitId ?? null,
    unitCode: defect.unitCode ?? defect.unit_code ?? task?.component?.unit?.code ?? null,
    unitName: defect.unitName ?? defect.unit_name ?? task?.component?.unit?.name ?? null,
    assignedToTeamId: defect.assignedToTeamId ?? defect.assigned_to_team_id ?? null,
    assignedToTeamName: defect.assignedToTeamName ?? defect.assigned_to_team_name ?? null,
    raisedById: defect.raisedById ?? defect.raised_by_id ?? null,
    raisedByName: defect.raisedByName ?? defect.raised_by_name ?? null,
  };
}

function normalizeStatus(status?: string): string {
  if (!status) return 'DRAFT';
  const key = status.toLowerCase();
  return BACKEND_LIFECYCLE_TO_FRONTEND[key] || status.toUpperCase();
}

function normalizePriority(priority?: string): Task['priority'] {
  if (!priority) return 'MEDIUM';
  return PRIORITY_TO_FRONTEND[priority.toLowerCase()] || 'MEDIUM';
}

function ageDays(createdAt: string): number {
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - created.getTime()) / 86400000));
}
